'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ArrowLeft, Sparkles, Bookmark, Share2, TrendingUp, ExternalLink, Copy, CheckCheck, Globe, UserCircle, RefreshCw, Users,  } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import MoodBoardComments from '@/app/fashion-discovery-interface/components/MoodBoardComments';
import PasswordGate from '@/components/ui/PasswordGate';



interface FeedOutfit {
  id: string;
  outfit: {
    editorialTitle: string;
    editorialSubtitle: string;
    aesthetic: string;
    stylistNotes: string;
    keyWords: string[];
    items: Array<{
      id: string;
      name: string;
      category: string;
      estimatedPrice: string;
      stylingNote: string;
      searchQuery: string;
      accentColor?: string;
    }>;
  };
  wizardData: {
    occasion: string;
    budget: number;
    currency: string;
    gender: string;
  };
  createdAt: string;
  shareCount: number;
  saveCount: number;
  createdBy: string | null;
  creatorName: string | null;
}

type FeedFilter = 'trending' | 'most-saved' | 'most-shared';
type FeedTab = 'discover' | 'following';

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: '£',
  USD: '$',
  EUR: '€',
  JPY: '¥',
  AUD: 'A$',
  CAD: 'C$',
};

function formatCurrency(amount: number, currency: string): string {
  const symbol = CURRENCY_SYMBOLS[currency] ?? currency;
  return `${symbol}${amount.toLocaleString()}`;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

export default function PublicFeedPage() {
  const router = useRouter();
  const { user } = useAuth();
  const supabase = createClient();

  const [activeTab, setActiveTab] = useState<FeedTab>('discover');
  const [outfits, setOutfits] = useState<FeedOutfit[]>([]);
  const [followingOutfits, setFollowingOutfits] = useState<FeedOutfit[]>([]);
  const [loading, setLoading] = useState(true);
  const [followingLoading, setFollowingLoading] = useState(false);
  const [filter, setFilter] = useState<FeedFilter>('trending');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [followingPage, setFollowingPage] = useState(0);
  const [followingHasMore, setFollowingHasMore] = useState(true);
  const [followingLoadingMore, setFollowingLoadingMore] = useState(false);
  const [followedCreatorIds, setFollowedCreatorIds] = useState<string[]>([]);

  const PAGE_SIZE = 12;

  const enrichOutfits = useCallback(
    async (data: any[]): Promise<FeedOutfit[]> => {
      if (!data || data.length === 0) return [];

      const titles = [...new Set(data.map((r: any) => r.outfit?.editorialTitle).filter(Boolean))];
      let saveCounts: Record<string, number> = {};

      if (titles.length > 0) {
        const { data: saveData } = await supabase
          .from('outfit_save_counts')
          .select('editorial_title, save_count')
          .in('editorial_title', titles);

        if (saveData) {
          saveCounts = Object.fromEntries(
            saveData.map((r: any) => [r.editorial_title, Number(r.save_count)])
          );
        }
      }

      const shareCounts: Record<string, number> = {};
      data.forEach((r: any) => {
        const title = r.outfit?.editorialTitle;
        if (title) shareCounts[title] = (shareCounts[title] ?? 0) + 1;
      });

      const creatorIds = [...new Set(data.map((r: any) => r.created_by).filter(Boolean))];
      let creatorNames: Record<string, string> = {};
      if (creatorIds.length > 0) {
        const { data: profileData } = await supabase
          .from('creator_profiles')
          .select('id, display_name')
          .in('id', creatorIds);
        if (profileData) {
          creatorNames = Object.fromEntries(profileData.map((p: any) => [p.id, p.display_name]));
        }
      }

      return data.map((r: any) => ({
        id: r.id,
        outfit: r.outfit,
        wizardData: r.wizard_data,
        createdAt: r.created_at,
        shareCount: shareCounts[r.outfit?.editorialTitle] ?? 1,
        saveCount: saveCounts[r.outfit?.editorialTitle] ?? 0,
        createdBy: r.created_by ?? null,
        creatorName: r.created_by ? (creatorNames[r.created_by] ?? null) : null,
      }));
    },
    [supabase]
  );

  const fetchFeed = useCallback(
    async (pageNum: number, replace: boolean) => {
      if (replace) setLoading(true);
      else setLoadingMore(true);

      try {
        const { data, error } = await supabase
          .from('shared_outfits')
          .select('id, outfit, wizard_data, created_at, created_by')
          .order('created_at', { ascending: false })
          .range(pageNum * PAGE_SIZE, (pageNum + 1) * PAGE_SIZE - 1);

        if (error || !data) {
          if (replace) setOutfits([]);
          return;
        }

        const mapped = await enrichOutfits(data);

        if (replace) {
          setOutfits(mapped);
        } else {
          setOutfits(prev => [...prev, ...mapped]);
        }
        setHasMore(data.length === PAGE_SIZE);
      } catch {
        if (replace) setOutfits([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [supabase, enrichOutfits]
  );

  const fetchFollowingFeed = useCallback(
    async (pageNum: number, replace: boolean, creatorIds: string[]) => {
      if (creatorIds.length === 0) {
        if (replace) setFollowingOutfits([]);
        setFollowingLoading(false);
        return;
      }

      if (replace) setFollowingLoading(true);
      else setFollowingLoadingMore(true);

      try {
        const { data, error } = await supabase
          .from('shared_outfits')
          .select('id, outfit, wizard_data, created_at, created_by')
          .in('created_by', creatorIds)
          .order('created_at', { ascending: false })
          .range(pageNum * PAGE_SIZE, (pageNum + 1) * PAGE_SIZE - 1);

        if (error || !data) {
          if (replace) setFollowingOutfits([]);
          return;
        }

        const mapped = await enrichOutfits(data);

        if (replace) {
          setFollowingOutfits(mapped);
        } else {
          setFollowingOutfits(prev => [...prev, ...mapped]);
        }
        setFollowingHasMore(data.length === PAGE_SIZE);
      } catch {
        if (replace) setFollowingOutfits([]);
      } finally {
        setFollowingLoading(false);
        setFollowingLoadingMore(false);
      }
    },
    [supabase, enrichOutfits]
  );

  // Fetch followed creator IDs when user is logged in
  useEffect(() => {
    if (!user) {
      setFollowedCreatorIds([]);
      return;
    }
    const loadFollows = async () => {
      const { data } = await supabase
        .from('creator_follows')
        .select('following_id')
        .eq('follower_id', user.id);
      const ids = data?.map((r: any) => r.following_id) ?? [];
      setFollowedCreatorIds(ids);
    };
    loadFollows();
  }, [user, supabase]);

  useEffect(() => {
    setPage(0);
    fetchFeed(0, true);
  }, [fetchFeed]);

  // Fetch following feed when tab switches to following or followed IDs change
  useEffect(() => {
    if (activeTab === 'following') {
      setFollowingPage(0);
      fetchFollowingFeed(0, true, followedCreatorIds);
    }
  }, [activeTab, followedCreatorIds, fetchFollowingFeed]);

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchFeed(next, false);
  };

  const handleFollowingLoadMore = () => {
    const next = followingPage + 1;
    setFollowingPage(next);
    fetchFollowingFeed(next, false, followedCreatorIds);
  };

  const sortOutfits = (list: FeedOutfit[]) =>
    [...list].sort((a, b) => {
      if (filter === 'most-saved') return b.saveCount - a.saveCount;
      if (filter === 'most-shared') return b.shareCount - a.shareCount;
      return b.saveCount + b.shareCount - (a.saveCount + a.shareCount);
    });

  const sortedOutfits = sortOutfits(outfits);
  const sortedFollowingOutfits = sortOutfits(followingOutfits);

  const handleCopyLink = async (id: string) => {
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL ?? 'https://atelier-ai.app';
    const url = `${siteUrl}/share/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // fallback
    }
  };

  const isFollowingTab = activeTab === 'following';
  const currentLoading = isFollowingTab ? followingLoading : loading;
  const currentOutfits = isFollowingTab ? sortedFollowingOutfits : sortedOutfits;
  const currentHasMore = isFollowingTab ? followingHasMore : hasMore;
  const currentLoadingMore = isFollowingTab ? followingLoadingMore : loadingMore;
  const handleCurrentLoadMore = isFollowingTab ? handleFollowingLoadMore : handleLoadMore;

  return (
    <PasswordGate>
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FAF7F2' }}>
        {/* Header */}
        <header
          className="sticky top-0 z-50 border-b"
          style={{ backgroundColor: '#FAF7F2', borderColor: 'hsl(35 20% 88%)' }}
        >
          <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-10">
            <div className="flex items-center justify-between h-14 sm:h-16">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => router.back()}
                  className="flex items-center gap-1.5 text-sm font-medium transition-all hover:opacity-70"
                  style={{ color: 'hsl(30 8% 52%)' }}
                  aria-label="Go back"
                >
                  <ArrowLeft size={15} />
                  <span className="hidden sm:inline">Back</span>
                </button>
              </div>

              <div className="flex items-center gap-2">
                <Globe size={14} style={{ color: '#C9A96E' }} />
                <span
                  className="text-xs sm:text-sm font-medium tracking-wide uppercase"
                  style={{ color: '#C9A96E', letterSpacing: '0.08em' }}
                >
                  Public Feed
                </span>
              </div>

              <div className="flex items-center gap-3">
                <Link href="/fashion-discovery-interface" className="flex items-center gap-1.5">
                  <AppLogo size={22} />
                  <span
                    className="hidden sm:inline font-serif-display text-base tracking-tight"
                    style={{ color: '#1A1A1A' }}
                  >
                    Atelier.ai
                  </span>
                </Link>
                {user && (
                  <Link
                    href="/account"
                    className="flex items-center gap-1.5 text-xs sm:text-sm font-medium transition-all hover:opacity-70"
                    style={{ color: 'hsl(30 8% 52%)' }}
                  >
                    <UserCircle size={20} style={{ color: '#C9A96E' }} />
                    <span className="hidden sm:inline">Account</span>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 max-w-screen-xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-8 sm:py-12">
          {/* Hero */}
          <div className="mb-8 sm:mb-10">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp size={16} style={{ color: '#C9A96E' }} />
              <span
                className="text-xs font-medium tracking-widest uppercase"
                style={{ color: '#C9A96E' }}
              >
                Community Mood Boards
              </span>
            </div>
            <h1
              className="font-serif-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-3"
              style={{ color: '#1A1A1A' }}
            >
              Most-Loved Looks
            </h1>
            <p className="text-sm sm:text-base max-w-xl" style={{ color: 'hsl(30 8% 52%)' }}>
              Discover the mood boards the community is saving and sharing — curated by AI, loved by
              real people.
            </p>
          </div>

          {/* Feed tabs (Discover / Following) */}
          <div className="flex items-center gap-1 mb-6">
            <button
              onClick={() => setActiveTab('discover')}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all"
              style={{
                backgroundColor: activeTab === 'discover' ? '#1A1A1A' : 'transparent',
                color: activeTab === 'discover' ? '#FAF7F2' : 'hsl(30 8% 52%)',
                border: activeTab === 'discover' ? 'none' : '1px solid hsl(35 20% 88%)',
              }}
            >
              <Globe size={13} />
              Discover
            </button>
            <button
              onClick={() => {
                if (!user) {
                  router.push('/login');
                  return;
                }
                setActiveTab('following');
              }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all"
              style={{
                backgroundColor: activeTab === 'following' ? '#1A1A1A' : 'transparent',
                color: activeTab === 'following' ? '#FAF7F2' : 'hsl(30 8% 52%)',
                border: activeTab === 'following' ? 'none' : '1px solid hsl(35 20% 88%)',
              }}
            >
              <Users size={13} />
              Following
            </button>
          </div>

          {/* Filter tabs */}
          <div className="flex items-center gap-2 mb-8 border-b" style={{ borderColor: 'hsl(35 20% 88%)' }}>
            {(
              [
                { key: 'trending', label: 'Trending', icon: TrendingUp },
                { key: 'most-saved', label: 'Most Saved', icon: Bookmark },
                { key: 'most-shared', label: 'Most Shared', icon: Share2 },
              ] as const
            ).map(({ key, label, icon: FilterIcon }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className="flex items-center gap-1.5 px-3 py-2.5 text-xs sm:text-sm font-medium border-b-2 transition-all -mb-px"
                style={{
                  borderBottomColor: filter === key ? '#C9A96E' : 'transparent',
                  color: filter === key ? '#1A1A1A' : 'hsl(30 8% 52%)',
                }}
              >
                <FilterIcon size={13} />
                {label}
              </button>
            ))}
          </div>

          {/* Feed grid */}
          {currentLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-2xl overflow-hidden animate-pulse"
                  style={{ backgroundColor: 'hsl(35 15% 92%)' }}
                >
                  <div className="h-48" style={{ backgroundColor: 'hsl(35 15% 88%)' }} />
                  <div className="p-5 space-y-3">
                    <div className="h-4 rounded w-3/4" style={{ backgroundColor: 'hsl(35 15% 88%)' }} />
                    <div className="h-3 rounded w-1/2" style={{ backgroundColor: 'hsl(35 15% 88%)' }} />
                    <div className="h-3 rounded w-2/3" style={{ backgroundColor: 'hsl(35 15% 88%)' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : currentOutfits.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              {isFollowingTab ? (
                <>
                  <Users size={32} style={{ color: '#C9A96E' }} className="mb-4 opacity-50" />
                  <p className="text-base font-medium mb-2" style={{ color: '#1A1A1A' }}>
                    {followedCreatorIds.length === 0
                      ? "You're not following anyone yet" : 'No mood boards from followed creators'}
                  </p>
                  <p className="text-sm mb-6" style={{ color: 'hsl(30 8% 52%)' }}>
                    {followedCreatorIds.length === 0
                      ? 'Follow creators to see their mood boards here.' : "The creators you follow haven't shared any mood boards yet."}
                  </p>
                  <button
                    onClick={() => setActiveTab('discover')}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all hover:opacity-90"
                    style={{ backgroundColor: '#1A1A1A', color: '#FAF7F2' }}
                  >
                    <Globe size={14} />
                    Browse Discover
                  </button>
                </>
              ) : (
                <>
                  <Sparkles size={32} style={{ color: '#C9A96E' }} className="mb-4 opacity-50" />
                  <p className="text-base font-medium mb-2" style={{ color: '#1A1A1A' }}>
                    No mood boards yet
                  </p>
                  <p className="text-sm mb-6" style={{ color: 'hsl(30 8% 52%)' }}>
                    Be the first to share a look with the community.
                  </p>
                  <Link
                    href="/fashion-discovery-interface"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all hover:opacity-90"
                    style={{ backgroundColor: '#1A1A1A', color: '#FAF7F2' }}
                  >
                    <Sparkles size={14} />
                    Create a Mood Board
                  </Link>
                </>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                {currentOutfits.map(item => (
                  <FeedCard
                    key={item.id}
                    item={item}
                    copiedId={copiedId}
                    onCopy={handleCopyLink}
                  />
                ))}
              </div>

              {currentHasMore && (
                <div className="flex justify-center mt-10">
                  <button
                    onClick={handleCurrentLoadMore}
                    disabled={currentLoadingMore}
                    className="flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium border transition-all hover:opacity-80 disabled:opacity-50"
                    style={{
                      borderColor: 'hsl(35 20% 82%)',
                      color: '#1A1A1A',
                      backgroundColor: 'transparent',
                    }}
                  >
                    {currentLoadingMore ? (
                      <RefreshCw size={14} className="animate-spin" />
                    ) : (
                      <RefreshCw size={14} />
                    )}
                    {currentLoadingMore ? 'Loading…' : 'Load More'}
                  </button>
                </div>
              )}
            </>
          )}
        </main>

      </div>
    </PasswordGate>
  );
}

interface FeedCardProps {
  item: FeedOutfit;
  copiedId: string | null;
  onCopy: (id: string) => void;
}

function FeedCard({ item, copiedId, onCopy }: FeedCardProps) {
  const { outfit, wizardData, createdAt, shareCount, saveCount, id, createdBy, creatorName } = item;
  const keywords = outfit?.keyWords ?? [];
  const items = outfit?.items ?? [];
  const totalPrice = items.reduce((sum, i) => {
    const num = parseFloat(i.estimatedPrice?.replace(/[^0-9.]/g, '') ?? '0');
    return sum + (isNaN(num) ? 0 : num);
  }, 0);

  const accentColors = items
    .map(i => i.accentColor)
    .filter(Boolean)
    .slice(0, 4) as string[];

  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL ?? 'https://atelier-ai.app';
  const shareUrl = `${siteUrl}/share/${id}`;

  return (
    <article
      className="group rounded-2xl overflow-hidden border flex flex-col transition-all duration-200 hover:shadow-md"
      style={{ backgroundColor: '#FFFFFF', borderColor: 'hsl(35 20% 88%)' }}
    >
      {/* Colour palette strip */}
      <div className="h-2 flex">
        {accentColors.length > 0 ? (
          accentColors.map((c, i) => (
            <div key={i} className="flex-1" style={{ backgroundColor: c }} />
          ))
        ) : (
          <div className="flex-1" style={{ backgroundColor: '#C9A96E' }} />
        )}
      </div>

      <div className="p-5 flex flex-col flex-1 gap-4">
        {/* Aesthetic badge + time */}
        <div className="flex items-center justify-between">
          {outfit?.aesthetic && (
            <span
              className="text-xs font-medium px-2.5 py-1 rounded-full"
              style={{ backgroundColor: 'hsl(35 30% 94%)', color: '#C9A96E' }}
            >
              {outfit.aesthetic}
            </span>
          )}
          <span className="text-xs ml-auto" style={{ color: 'hsl(30 8% 62%)' }}>
            {timeAgo(createdAt)}
          </span>
        </div>

        {/* Title & subtitle */}
        <div>
          <h2
            className="font-serif-display text-lg sm:text-xl leading-snug tracking-tight mb-1"
            style={{ color: '#1A1A1A' }}
          >
            {outfit?.editorialTitle ?? 'Untitled Look'}
          </h2>
          {outfit?.editorialSubtitle && (
            <p className="text-xs sm:text-sm line-clamp-2" style={{ color: 'hsl(30 8% 52%)' }}>
              {outfit.editorialSubtitle}
            </p>
          )}
        </div>

        {/* Creator snippet: occasion + budget */}
        <div
          className="flex items-center gap-3 text-xs rounded-xl px-3 py-2.5"
          style={{ backgroundColor: 'hsl(35 15% 96%)', color: 'hsl(30 8% 52%)' }}
        >
          {wizardData?.occasion && (
            <span className="capitalize truncate">{wizardData.occasion}</span>
          )}
          {wizardData?.occasion && totalPrice > 0 && (
            <span style={{ color: 'hsl(35 20% 80%)' }}>·</span>
          )}
          {totalPrice > 0 && wizardData?.currency && (
            <span className="font-medium" style={{ color: '#1A1A1A' }}>
              {formatCurrency(Math.round(totalPrice), wizardData.currency)} est.
            </span>
          )}
          {wizardData?.gender && (
            <>
              <span style={{ color: 'hsl(35 20% 80%)' }}>·</span>
              <span className="capitalize">{wizardData.gender}</span>
            </>
          )}
        </div>

        {/* Creator profile link */}
        {createdBy && (
          <Link
            href={`/creator/${createdBy}`}
            className="flex items-center gap-1.5 text-xs transition-all hover:opacity-70 self-start"
            style={{ color: 'hsl(30 8% 52%)' }}
            aria-label={`View ${creatorName ?? 'creator'}'s profile`}
          >
            <UserCircle size={13} style={{ color: '#C9A96E' }} />
            <span style={{ color: '#C9A96E' }}>{creatorName ?? 'View creator'}</span>
          </Link>
        )}

        {/* Keywords */}
        {keywords.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {keywords.slice(0, 4).map((kw, i) => (
              <span
                key={i}
                className="text-xs px-2 py-0.5 rounded-full border"
                style={{
                  borderColor: 'hsl(35 20% 88%)',
                  color: 'hsl(30 8% 52%)',
                  backgroundColor: 'transparent',
                }}
              >
                {kw}
              </span>
            ))}
          </div>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Stats row */}
        <div
          className="flex items-center gap-4 pt-3 border-t text-xs"
          style={{ borderColor: 'hsl(35 20% 92%)' }}
        >
          <div className="flex items-center gap-1.5" style={{ color: 'hsl(30 8% 52%)' }}>
            <Bookmark size={12} style={{ color: '#C9A96E' }} />
            <span>
              <strong style={{ color: '#1A1A1A' }}>{saveCount}</strong> saved
            </span>
          </div>
          <div className="flex items-center gap-1.5" style={{ color: 'hsl(30 8% 52%)' }}>
            <Share2 size={12} style={{ color: '#C9A96E' }} />
            <span>
              <strong style={{ color: '#1A1A1A' }}>{shareCount}</strong> shared
            </span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {/* Copy link */}
            <button
              onClick={() => onCopy(id)}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all hover:opacity-80"
              style={{
                borderColor: 'hsl(35 20% 88%)',
                color: copiedId === id ? '#C9A96E' : 'hsl(30 8% 52%)',
                backgroundColor: copiedId === id ? 'hsl(35 30% 94%)' : 'transparent',
              }}
              aria-label="Copy link"
            >
              {copiedId === id ? <CheckCheck size={11} /> : <Copy size={11} />}
              {copiedId === id ? 'Copied' : 'Copy'}
            </button>

            {/* View link */}
            <Link
              href={`/share/${id}`}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all hover:opacity-80"
              style={{ backgroundColor: '#1A1A1A', color: '#FAF7F2' }}
              aria-label="View mood board"
            >
              <ExternalLink size={11} />
              View
            </Link>
          </div>
        </div>
      </div>

      {/* Comments section */}
      <MoodBoardComments outfitId={id} creatorId={createdBy ?? null} compact={true} />
    </article>
  );
}
